/**
 * WebSocket-Grundlage (Spec 2 „Realtime", Phase 1) und Presence (Spec 7.1).
 *
 * Bewusst wenige Ereignistypen. `ticket.updated` sagt nur „an diesem Ticket
 * hat sich etwas geaendert" — ob Status, Zuweisung, Tags oder Verlauf,
 * interessiert den Client nicht, er laedt neu.
 *
 * Phase 4 ergaenzt `ai.draft_updated`: der Client wartet nach dem Klick auf
 * „KI-Entwurf" auf genau dieses Ereignis, statt zu pollen.
 *
 * Phase 5 ergaenzt `presence.updated` und macht den Kanal damit **zweiseitig**
 * (Spec 7.1: „X liest gerade", „X tippt gerade"). Der Client schickt
 * {@link clientRealtimeMessageSchema}, der Server antwortet mit der
 * vollstaendigen Anwesenheitsliste des Tickets — kein Delta, weil die Liste
 * bei zwei bis fuenf Agents ohnehin kuerzer ist als jede Aenderungsbeschreibung.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
export const REALTIME_EVENTS = ['ticket.updated', 'ai.draft_updated', 'presence.updated'];
export const realtimeEventTypeSchema = z.enum(REALTIME_EVENTS);
/**
 * Ein Agent an einem Ticket. `editing` ist das weiche Lock aus Spec 7.1
 * („in Bearbeitung durch X"), `typing` das kurzlebige Signal aus dem Editor.
 */
export const presenceEntrySchema = z.object({
    member_id: uuidSchema,
    name: z.string(),
    /** Antwort-Editor offen — Grundlage des Kollisionshinweises. */
    editing: z.boolean(),
    /** Tippt gerade (throttled, laeuft nach Sekunden von selbst aus). */
    typing: z.boolean(),
    since: z.iso.datetime(),
});
export const realtimeEventSchema = z.object({
    type: realtimeEventTypeSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    /** Fuer die Scope-Pruefung auf dem Server, bevor das Ereignis rausgeht. */
    queue_id: uuidSchema,
    source_id: uuidSchema.nullable(),
    /** Nur bei `ai.draft_updated` gesetzt (Spec 9.2). */
    draft_id: uuidSchema.nullable().default(null),
    /** Nur bei `presence.updated` gefuellt (Spec 7.1) — die komplette Liste. */
    presence: z.array(presenceEntrySchema).default([]),
    at: z.iso.datetime(),
});
// ────────────────────────────────── Gegenrichtung: Client → Server (7.1) ────
/**
 * Was ein Client schicken darf. Bewusst winzig: eintreten, Lebenszeichen,
 * verlassen. Alles andere laeuft ueber die REST-API — ein WebSocket ist hier
 * kein zweiter Schreibweg (`security.md`: eine Grenze, nicht zwei).
 */
export const CLIENT_REALTIME_MESSAGES = ['presence.join', 'presence.ping', 'presence.leave'];
export const clientRealtimeMessageTypeSchema = z.enum(CLIENT_REALTIME_MESSAGES);
export const clientRealtimeMessageSchema = z.object({
    type: clientRealtimeMessageTypeSchema,
    ticket_id: uuidSchema,
    editing: z.boolean().default(false),
    typing: z.boolean().default(false),
});
/**
 * Nach dieser Zeit ohne Lebenszeichen gilt ein Agent als weg (Spec 7.1:
 * „weiches Lock mit Timeout ~5 Min ohne Aktivitaet; kein hartes Lock").
 */
export const PRESENCE_TIMEOUT_MS = 5 * 60_000;
/** Wie oft der Client ein Lebenszeichen schickt — deutlich unter dem Timeout. */
export const PRESENCE_HEARTBEAT_MS = 45_000;
/** „Tippt gerade" verfaellt schnell von selbst, auch ohne `presence.leave`. */
export const PRESENCE_TYPING_TIMEOUT_MS = 10_000;
//# sourceMappingURL=realtime.js.map